import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldAlt, faSpinner, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import authService from '../services/authService';
import { useLanguage } from '../contexts/LanguageContext';
import { useBranding } from '../contexts/BrandingContext';

const Login = ({ onLogin }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { logo, appName, primaryColor, footerText } = useBranding();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const user = await authService.login(email, password);
      if (onLogin) {
        onLogin(user);
      }

      // Redirect based on user role
      if (user.role === 'blue_team') {
        navigate('/blue-team');
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err.response?.data?.message || t('auth.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center">
            {logo ? (
              <img
                src={logo}
                alt={appName}
                className="h-20 w-auto max-w-[200px] object-contain"
              />
            ) : (
              <div className="bg-gradient-to-r from-red-600 p-4 rounded-full" style={{ backgroundImage: `linear-gradient(to right, #dc2626, ${primaryColor})` }}>
                <FontAwesomeIcon icon={faShieldAlt} className="h-12 w-12 text-white" />
              </div>
            )}
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
            {t('auth.loginTitle')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-400">
            {t('auth.loginSubtitle')}
          </p>
        </div>

        <div className="mt-8 bg-gray-800 py-8 px-6 shadow-xl rounded-lg border border-gray-700">
          {error && (
            <div className="mb-4 bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded flex items-center">
              <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
              {error}
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300">
                {t('auth.email')}
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-600 rounded-md shadow-sm bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent"
                  style={{ '--tw-ring-color': primaryColor }}
                  placeholder={t('auth.email')}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300">
                {t('auth.password')}
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-600 rounded-md shadow-sm bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent"
                  style={{ '--tw-ring-color': primaryColor }}
                  placeholder={t('auth.password')}
                />
              </div>
              <div className="mt-2 text-right">
                <Link to="/forgot-password" className="text-sm hover:opacity-80" style={{ color: primaryColor }}>
                  {t('auth.forgotPassword')}
                </Link>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: primaryColor }}
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                    {t('auth.signingIn')}
                  </>
                ) : (
                  t('auth.login')
                )}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-600" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-gray-800 text-gray-400">{t('auth.newToplatform')}</span>
              </div>
            </div>

            <div className="mt-6">
              <Link
                to="/register"
                className="w-full flex justify-center py-2 px-4 border border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2"
              >
                {t('auth.createAccount')}
              </Link>
            </div>
          </div>
        </div>

        {/* Team indicators */}
        <div className="flex justify-center space-x-8 text-sm">
          <div className="flex items-center text-red-400">
            <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
            {t('nav.redTeam')}
          </div>
          <div className="flex items-center text-blue-400">
            <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
            {t('nav.blueTeam')}
          </div>
          <div className="flex items-center" style={{ color: primaryColor }}>
            <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: primaryColor }}></div>
            {t('nav.purpleTeam')}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-gray-500 mt-8">
          <p>{footerText || `${appName}. Purple Team Operations by Zerberos`}</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
